import { createSelector } from 'reselect';
import {
  sourceSelector,
  destinationSelector,
  departureSelector,
  arrivalSelector,
  passengerSelector,
  flightSearchDataSelector,
  journeyTypeSelector,
  travelTypeSelector,
  internationalRoundTripSelector,
  domesticRoundTripSelector,
  isRoundTripSelector,
  flavourSelector,
  selectedFareIdentifierSelector
} from '../../Home/redux/selectors';
import { smartEngageBannerSelector } from '../../SmartEngage/redux/selectors';
import {
  filterFunction,
  sortFunction,
  getTempSrcDestObj,
  getSortOptionDetails,
  filterPureImpureFlights
} from '../utils/SRPThorUtils';
import {
  isObjectEmpty,
  getUrlParam,
  getSrpQueryParams,
  getSrpQueryParamsForCityCountry,
  getFareKey,
  handleListWithSpillOverBanner
} from '../../utils/common/utils';
import { __GI_CB__, __ADTECH_INFO_URL__ } from '@stencil/flights-constants/src/urls';
import {
  BANNER_POSITIONS,
  DWEB_BANNER_POSITIONS
} from '@stencil/flights-constants/src/default-values';
import { REPLACER } from '@stencil/flights-constants/src/text';
import sendDataToNewrelic from '@stencil/send-data-to-new-relic';
import { getFromUrlObject } from '../../Home/utils/searchDataUtils';
import getDateObject from '@stencil/flights-date-utils/src/get-date-object';
import { tokenSelector } from '../../BookingReviewPage/redux/selectors';
import { userSelector } from '@stencil/user-reducer';
import { filterPreselectedFlight } from '../utils/filterUtils';
import { searchStringToObject } from '../../BookingReviewPage/utils/reviewPageUtils';
import { globalSiteStateSelector } from '../../GlobalSite/redux/selectors';

export const srpStateSelector = state => state.srp;
export const brpStateSelector = state => state.brp;

export const srpErrorMsgSelector = createSelector(srpStateSelector, srp => srp.errorMsg);

export const directlyLandedSelector = createSelector(srpStateSelector, srp => srp.directlyLanded);

export const isStoreEmptySelector = createSelector(srpStateSelector, srp => srp.isStoreEmpty);

export const srpMetaSelector = createSelector(srpStateSelector, srp => srp.meta);

export const onLoadSelector = createSelector(srpStateSelector, srp => srp.onLoad);

export const flightComparisonDataSelector = createSelector(
  srpStateSelector,
  srp => srp.flightComaparison
);

export const flightsToCompareListSelector = createSelector(
  srpStateSelector,
  srp => srp.flightsToCompareList
);

export const nudgeToShowSelector = createSelector(srpStateSelector, srp => srp.nudgeToShow);

export const srpMetaReviewQueryParamsSelector = createSelector([srpMetaSelector], meta => {
  const reviewQueryPrams = JSON.parse(meta?.additionalQueryParams?.REVIEW || null);
  if (reviewQueryPrams && Object.keys(reviewQueryPrams)) {
    return Object.keys(reviewQueryPrams)
      .map(key => `${key}=${reviewQueryPrams[key]}`)
      .join('&');
  }

  return '';
});

export const isDWebSelector = createSelector(directlyLandedSelector, direct =>
  Boolean(direct.isDWeb || (typeof window !== 'undefined' && window.flavour !== 'PWA'))
);

export const isExtraSeatAppliedSelector = createSelector(
  directlyLandedSelector,
  directlyLanded => directlyLanded.isDirectlyLanded && directlyLanded.searchString.includes('ft=e')
);

// TODO: check for removing __CLIENT__ check
export const isRescheduleSelector = createSelector(
  [directlyLandedSelector, brpStateSelector],
  (directlyLanded, brp) =>
    (directlyLanded.isDirectlyLanded && directlyLanded.searchString.includes('gds_resch')) ||
    (brp.urlParams && brp.urlParams.gds_resch) ||
    (__CLIENT__ && location.search.includes('gds_resch'))
);

export const isSEMSelector = createSelector(
  directlyLandedSelector,
  directlyLanded =>
    directlyLanded.isDirectlyLanded &&
    directlyLanded.searchString.includes('utm_source=google') &&
    directlyLanded.searchString.includes('utm_medium=cpc')
);

export const isSEOSelector = createSelector(
  directlyLandedSelector,
  directlyLanded =>
    directlyLanded.isDirectlyLanded &&
    directlyLanded.searchString.includes('gi_source=google') &&
    directlyLanded.searchString.includes('gi_medium=organic')
);

export const urlQuerySelector = createSelector([flightSearchDataSelector], flightSearchData => {
  return getUrlParam(flightSearchData);
});

export const specialFareMetaQueryParamsSelector = createSelector(srpStateSelector, srp => {
  return srp.specialFareMetaQueryParams || {};
});

export const constructThorUrl = createSelector(
  [
    flightSearchDataSelector,
    directlyLandedSelector,
    isRescheduleSelector,
    selectedFareIdentifierSelector,
    specialFareMetaQueryParamsSelector,
    flavourSelector
  ],
  (
    flightSearchData,
    directlyLanded,
    isReschedule,
    fareTypeIdentifier,
    specialFareMetaQueryParams,
    flavour
  ) => {
    const {
      source,
      destination,
      departure,
      arrival,
      passengers,
      flightClass,
      journeyType,
      travelType
    } = flightSearchData;
    if (source.iata === destination.iata) {
      return null;
    }
    if (arrival && departure && arrival.dateObj < departure.dateObj) {
      return null;
    }
    const query = `air-${getSrpQueryParams(flightSearchData, true)}`;
    let searchParams = '';

    if (directlyLanded.isDirectlyLanded && directlyLanded.searchString) {
      directlyLanded.searchString
        .replace('?', '')
        .split('&')
        .forEach(p => {
          searchParams += '&' + (p === 'sf=true' ? 'ft=s' : p);
        });
    } else if (__CLIENT__) {
      // TODO: check for removing __CLIENT__ check
      if (location.search) {
        location.search
          .replace('?', '')
          .split('&')
          .forEach(p => {
            searchParams += '&' + (p === 'sf=true' ? 'ft=s' : p);
          });
      } else {
        if (fareTypeIdentifier) {
          searchParams = `&${fareTypeIdentifier === 'sf=true' ? 'ft=s' : fareTypeIdentifier}`;
        }
      }
    }

    if (
      specialFareMetaQueryParams &&
      typeof specialFareMetaQueryParams === 'object' &&
      Object.keys(specialFareMetaQueryParams).length
    ) {
      for (let key in specialFareMetaQueryParams) {
        searchParams += `&${key}=${encodeURIComponent(specialFareMetaQueryParams[key])}`;
      }
    }

    let thorUrl = `${__GI_CB__}/v2/thor/rest/flight/search/${
      travelType === 'international' ? 'int' : ''
    }?application=fbs&flavour=${flavour}&mime=html&script=y&actionData=[{%22query%22:%22${query}%22}]&slotfl=y1&nearbyfl=y1&ps=true${searchParams}`;

    if (isReschedule) {
      thorUrl += '&noMulti=y';
    }

    return thorUrl;
  }
);

export const constructAdTechBannerUrlSelector = createSelector(
  flightSearchDataSelector,
  flightSearchData => {
    const query = `air-${getSrpQueryParams(flightSearchData, true)}`;
    return `${__ADTECH_INFO_URL__}&q=${query}`;
  }
);

export const prefetchedChunksSelector = createSelector(srpStateSelector, srp => srp.chunks);

export const hasPrefetchedChunksCalledSelector = createSelector(
  prefetchedChunksSelector,
  chunks => {
    return chunks.prefetchChunks;
  }
);

export const hasPrefetchedChunksSelector = createSelector(prefetchedChunksSelector, chunks => {
  return chunks.allChunks;
});

export const dataIndexSelector = createSelector(srpStateSelector, srp => srp.dataIndex);

export const unorderedChunksSelector = createSelector(srpStateSelector, srp => srp.unorderedChunks);

export const unorderedChunksLengthSelector = createSelector(
  srpStateSelector,
  srp => Object.keys(srp.unorderedChunks).length
);

export const searchContextDetailsSelector = createSelector(
  srpStateSelector,
  srp => srp?.searchContext || null
);

export const displayOnwardFlightsSelector = createSelector(
  srpStateSelector,
  srp => srp.displayOnwardFlights
);

export const displayReturnFlightsSelector = createSelector(
  srpStateSelector,
  srp => srp.displayReturnFlights
);

export const displayAlternateTimeFlightsSelector = createSelector(
  srpStateSelector,
  srp => srp.displayAlternateTimeFlights
);

export const displayAlternateLocationFlightsSelector = createSelector(
  srpStateSelector,
  srp => srp.displayAlternateLocationFlights
);

export const displayAlternateTimeFlightsLengthSelector = createSelector(
  displayAlternateTimeFlightsSelector,
  flights => flights.length
);

export const displayAlternateLocationFlightsLengthSelector = createSelector(
  displayAlternateLocationFlightsSelector,
  flights => flights.length
);

export const onwardFlightsCurrentLengthSelector = createSelector(
  srpStateSelector,
  srp => srp.onwardFlights.length
);

export const returnFlightsCurrentLengthSelector = createSelector(
  srpStateSelector,
  srp => srp.returnFlights.length
);

export const returnFlightsSpecialRTSelector = createSelector(
  srpStateSelector,
  srp => srp.specialRT
);

export const queryParamSelector = createSelector(srpStateSelector, srp => srp.queryParam);
export const searchStringSelector = createSelector(
  directlyLandedSelector,
  directlyLanded => directlyLanded.searchString
);
export const staticDataSelector = createSelector(srpStateSelector, srp => srp.staticData);
export const airportsSelector = createSelector(srpStateSelector, srp => srp.airports);
export const carriersSelector = createSelector(srpStateSelector, srp => srp.carriers);
export const aircraftsSelector = createSelector(srpStateSelector, srp => srp.aircrafts);
export const datesSelector = createSelector(srpStateSelector, srp => srp.dates);
export const discountsSelector = createSelector(srpStateSelector, srp => srp.discounts);
export const ibpSelector = createSelector(srpStateSelector, srp => srp.ibp);
export const terminalInfoSelector = createSelector(srpStateSelector, srp => srp.terminalInfo);
export const promotionTextSelector = createSelector(srpStateSelector, srp => srp.promotionText);
export const multiIndexSelector = createSelector(srpStateSelector, srp => srp.multiIndex);
export const refundabilitySelector = createSelector(srpStateSelector, srp => srp.refundability);
export const brandIdsSelector = createSelector(srpStateSelector, srp => srp.brandIds);
export const brandNamesSelector = createSelector(srpStateSelector, srp => srp.brandNames);
export const discountFaresSelector = createSelector(srpStateSelector, srp => srp.discountFares);
export const discountNamesSelector = createSelector(srpStateSelector, srp => srp.discountNames);
export const discountMessagesSelector = createSelector(
  srpStateSelector,
  srp => srp.discountMessages
);
export const paValSelector = createSelector(srpStateSelector, srp => srp.paVal);
export const messageValuesSelector = createSelector(srpStateSelector, srp => srp.messageValues);
export const messageIconsSelector = createSelector(srpStateSelector, srp => srp.messageIcons);
export const staticInformationSelector = createSelector(
  srpStateSelector,
  srp => srp.staticInformation
);
export const correlationIdForLoggingSelector = createSelector(
  [srpStateSelector, brpStateSelector],
  (srp, brpState) =>
    srp.correlationIdForLogging ||
    (brpState?.brpData?.query && brpState?.brpData?.query['search-id']) ||
    brpState?.bookingReviewData?.crid ||
    ''
);
export const commonFareRulesDataSelector = createSelector(
  srpStateSelector,
  srp => srp.commonFareRulesData
);
export const isInternationalSelector = createSelector(
  queryParamSelector,
  query => query && query.split('-')[8].includes('I')
);

export const isEconomyClassSelector = createSelector(
  queryParamSelector,
  query => query && query.split('-')[7].includes('E')
);

export const showFareTrendsSelector = createSelector(
  [isEconomyClassSelector, isRescheduleSelector],
  (isEconomy, isReschedule) => isEconomy && !isReschedule
);

export const alternateFlightOptionsSelector = createSelector(
  srpStateSelector,
  srp => srp.alternateFlightOptions
);

export const receivedAlternateOptionsApiResponseSelector = createSelector(
  srpStateSelector,
  srp => srp.receivedAlternateOptionsApiResponse
);

export const offerOverlaySelector = createSelector(srpStateSelector, srp => srp.overlayData);

export const filterSelector = createSelector(srpStateSelector, srp => srp.filterSettings);

export const isFilterAppliedSelector = createSelector(filterSelector, filter => {
  const filterKeys = Object.keys(filter);
  for (let i = 0; i < filterKeys.length; i++) {
    const filterSetting = filter[filterKeys[i]];
    if (filterSetting.values.length > 0) {
      return true;
    }
  }
  return false;
});

export const filterMinMaxSelector = createSelector(srpStateSelector, srp => srp.filterMinMaxValues);
export const onwardMaxPriceSelector = createSelector(srpStateSelector, srp => srp.onwardMaxPrice);
export const onwardMinPriceSelector = createSelector(srpStateSelector, srp => srp.onwardMinPrice);
export const returnMaxPriceSelector = createSelector(srpStateSelector, srp => srp.returnMaxPrice);
export const returnMinPriceSelector = createSelector(srpStateSelector, srp => srp.returnMinPrice);
export const onwardDurationSelector = createSelector(srpStateSelector, srp => srp.onwardDuration);
export const returnDurationSelector = createSelector(srpStateSelector, srp => srp.returnDuration);
export const footerDataSelector = createSelector(srpStateSelector, srp => srp.footerData);
export const bannerDetailsSelector = createSelector(srpStateSelector, srp => srp.bannerDetails);
export const bannerDetailsV2Selector = createSelector(srpStateSelector, srp => srp.bannerDetailsV2);
export const sponsoredFlightsSelector = createSelector(
  srpStateSelector,
  srp => srp.sponsoredFlights
);
export const listingBannerDetailsSelector = createSelector(
  srpStateSelector,
  srp => srp.listingBannerDetails
);
export const topBannerDetailsSelector = createSelector(
  srpStateSelector,
  srp => srp.topBannerDetails
);

export const carriersFilterSelector = createSelector(
  filterSelector,
  filter => filter.carriers.values
);

export const isPureImpureListingRequiredSelector = createSelector(
  [isDWebSelector, carriersFilterSelector],
  (isDweb, carrierFilter) => {
    return isDweb && carrierFilter?.length > 0;
  }
);

export const onwardFilterSelector = createSelector(
  [filterSelector, journeyTypeSelector, travelTypeSelector, isPureImpureListingRequiredSelector],
  (filter, journeyType, travelType, isPureImpureListingRequired) => ({
    ...(!isPureImpureListingRequired ? { carriers: filter.carriers } : null),
    onwardStops: filter.onwardStops,
    onwardTimePeriod: filter.onwardTimePeriod,
    price: filter.price,
    duration: filter.duration,
    layovers: filter.layovers,
    destinations: filter.destinations,
    returnLayovers: filter.returnLayovers,
    returnDestinations: filter.returnDestinations,
    refundability: filter.refundability,
    hideMultiAirline: filter.hideMultiAirline,
    nearbyFilter: filter.nearbyFilter,
    ...(journeyType === 'rt' && travelType === 'international'
      ? {
          returnDuration: filter.returnDuration,
          returnStops: filter.returnStops,
          returnTimePeriod: filter.returnTimePeriod,
          returnNearbyFilter: filter.returnNearbyFilter
        }
      : null)
  })
);

export const returnFilterSelector = createSelector(
  [filterSelector, isPureImpureListingRequiredSelector],
  (filter, isPureImpureListingRequired) => ({
    ...(!isPureImpureListingRequired ? { carriers: filter.carriers } : null),
    returnStops: filter.returnStops,
    returnTimePeriod: filter.returnTimePeriod,
    price: filter.returnPrice,
    returnDuration: filter.returnDuration,
    refundability: filter.refundability,
    hideMultiAirline: filter.hideMultiAirline,
    returnNearbyFilter: filter.returnNearbyFilter
  })
);

export const isOnwardFilterAppliedSelector = createSelector(
  [filterSelector, onwardFilterSelector, isPureImpureListingRequiredSelector],
  (filters, onwardFilter, isPureImpureListingRequired) => {
    const allFilters = {
      ...onwardFilter,
      ...(isPureImpureListingRequired ? { carriers: filters.carriers } : null)
    };
    const filterKeys = Object.keys(allFilters);
    for (let i = 0; i < filterKeys.length; i++) {
      const filterSetting = allFilters[filterKeys[i]];
      if (filterSetting.values.length > 0) {
        return true;
      }
    }
    return false;
  }
);

export const isReturnFilterAppliedSelector = createSelector(
  [filterSelector, returnFilterSelector, isPureImpureListingRequiredSelector],
  (filter, returnFilter, isPureImpureListingRequired) => {
    const allFilters = {
      ...returnFilter,
      ...(isPureImpureListingRequired ? { carriers: filter.carriers } : null)
    };
    const filterKeys = Object.keys(allFilters);
    for (let i = 0; i < filterKeys.length; i++) {
      const filterSetting = allFilters[filterKeys[i]];
      if (filterSetting.values.length > 0) {
        return true;
      }
    }
    return false;
  }
);

export const onwardFlightsSelector = createSelector(srpStateSelector, srp => srp.onwardFlights);
export const returnFlightsSelector = createSelector(srpStateSelector, srp => srp.returnFlights);

export const alternateTimeFlightsSelector = createSelector(
  srpStateSelector,
  srp => srp.alternateTimeFlights
);
export const alternateLocationFlightsSelector = createSelector(
  srpStateSelector,
  srp => srp.alternateLocationFlights
);

export const sortSelector = createSelector(srpStateSelector, srp => srp.sortSettings);
export const returnSortSelector = createSelector(srpStateSelector, srp => srp.returnSortSettings);

export const isSortAndFilterAppliedSelector = createSelector(
  [sortSelector, isFilterAppliedSelector],
  (sortSettings, isFilterApplied) => {
    let isSortApplied = true;
    if (sortSettings.currentSettings === 'cheapest' && sortSettings.isAscending === true) {
      isSortApplied = false;
    }
    return isSortApplied && isFilterApplied;
  }
);

export const onwardSortOptionsDetailsSelector = createSelector(
  [displayOnwardFlightsSelector],
  displayOnwardFlights => {
    return getSortOptionDetails(displayOnwardFlights);
  }
);

export const numberOfNonStopFlightsSelector = createSelector(
  srpStateSelector,
  srp => srp.numberOfNonStopFlights
);

export const numberOfNonStopReturnFlightsSelector = createSelector(
  srpStateSelector,
  srp => srp.numberOfNonStopReturnFlights
);

export const returnSortOptionsDetailsSelector = createSelector(
  [displayReturnFlightsSelector],
  displayReturnFlights => {
    return getSortOptionDetails(displayReturnFlights);
  }
);

export const displayInfoSelector = createSelector(srpStateSelector, srp => ({
  refundability: srp.refundability,
  airports: srp.airports,
  carriers: srp.carriers,
  dates: srp.dates,
  messageText: srp.messageText,
  messageValues: srp.messageValues,
  messageIcons: srp.messageIcons,
  discountFares: srp.discountFares,
  discountMessages: srp.discountMessages,
  showOnePaxPrice: srp.showOnePaxPrice,
  fareFamilyOrder: srp.fareFamilyOrder
}));

export const showOnePaxPriceSelector = createSelector(srpStateSelector, srp => srp.showOnePaxPrice);

export const receivedAllChunksSelector = createSelector(
  srpStateSelector,
  srp => srp.receivedAllChunks
);

export const showPriceLoaderSelector = createSelector(srpStateSelector, srp => srp.showPriceLoader);

export const specialFareMetaDetailsSelector = createSelector(srpStateSelector, srp => {
  return srp.specialFareMetaDetails;
});

export const nonDSRTPreselectedFlightSelector = createSelector(
  [displayOnwardFlightsSelector, specialFareMetaDetailsSelector, domesticRoundTripSelector],
  (displayOnwardFlights, specialFareMetaDetails, isDomesticRoundTrip) => {
    if (
      !isDomesticRoundTrip &&
      specialFareMetaDetails?.onwardFlightIndex > -1 &&
      displayOnwardFlights?.[specialFareMetaDetails.onwardFlightIndex]
    ) {
      return displayOnwardFlights[specialFareMetaDetails.onwardFlightIndex];
    }
    return null;
  }
);

export const getSRPAdTechBannerDetailsSelector = createSelector(
  srpStateSelector,
  srp => srp.srpAdTechBannerData
);

export const isVirtualAirportSelector = createSelector(
  [staticInformationSelector, flightSearchDataSelector],
  (staticInformation, flightSearchData) => {
    const destination = flightSearchData?.destination?.iata;
    return Boolean(destination && staticInformation?.['vad']?.['aqm']?.[destination]?.['ivap']);
  }
);

export const nearByDestinationAvailableSelector = createSelector(
  srpStateSelector,
  srp => srp.isNearByDestinationAvailable || false
);

export const nearbyFilterDetailsSelector = createSelector(
  staticInformationSelector,
  staticInformation => staticInformation?.sfd
);

export const isNearbyFilterVisibleSelector = createSelector(
  [isVirtualAirportSelector, nearByDestinationAvailableSelector, nearbyFilterDetailsSelector],
  (isVirtualAirport, isNearByDestinationAvailable, nearbyFilterDetails) =>
    Boolean(!isVirtualAirport && isNearByDestinationAvailable && nearbyFilterDetails?.ibt)
);

export const isNearbyFilterBannerVisibleSelector = createSelector(
  [isNearbyFilterVisibleSelector, domesticRoundTripSelector, isDWebSelector],
  (isNearbyFilterVisible, isDomesticRoundTrip, isDWeb) =>
    Boolean(isNearbyFilterVisible && !isDomesticRoundTrip && isDWeb)
);

export const filteredDisplayOnwardFlights = createSelector(
  [
    displayOnwardFlightsSelector,
    onwardFilterSelector,
    isPureImpureListingRequiredSelector,
    carriersFilterSelector,
    nonDSRTPreselectedFlightSelector
  ],
  (
    displayOnwardFlights,
    filterSettings,
    isPureImpureListingRequired,
    carrierFilter,
    preselectedFlight
  ) => {
    const newFilterSetting = { ...filterSettings };
    if (preselectedFlight) {
      newFilterSetting['preSelectedFilter'] = {
        values: preselectedFlight,
        filterFunc: filterPreselectedFlight
      };
    }
    if (isPureImpureListingRequired) {
      return filterPureImpureFlights(displayOnwardFlights, newFilterSetting, carrierFilter);
    }
    return filterFunction(displayOnwardFlights, newFilterSetting);
  }
);

export const filteredDisplayOnwardFlightsLength = createSelector(
  [filteredDisplayOnwardFlights, isPureImpureListingRequiredSelector],
  (displayOnwardFlights, isPureImpureListingRequired) => {
    if (isPureImpureListingRequired) {
      const [pureFlights = [], impureFlights = []] = displayOnwardFlights || [];
      return pureFlights.length + impureFlights.length;
    }
    if (
      displayOnwardFlights[displayOnwardFlights.length - 1] &&
      displayOnwardFlights[displayOnwardFlights.length - 1].originalIndex === 'viewAllFlights'
    ) {
      displayOnwardFlights.pop();
    }
    return (displayOnwardFlights || []).length;
  }
);

export const filteredAndSortedDisplayOnwardFlights = createSelector(
  [
    filteredDisplayOnwardFlights,
    sortSelector,
    isExtraSeatAppliedSelector,
    isPureImpureListingRequiredSelector
  ],
  (displayOnwardFlights, sortSettings, isExtraSeatApplied, isPureImpureListingRequired) => {
    try {
      if (isPureImpureListingRequired) {
        const [pureFlights, impureFlights] = displayOnwardFlights;
        let pureSortedList = sortFunction([...pureFlights], sortSettings);
        let impureSortedList = sortFunction([...impureFlights], sortSettings);
        if (isExtraSeatApplied) {
          pureSortedList = pureSortedList.filter(f => !f.isExtraSeatFlight);
          impureSortedList = impureSortedList.filter(f => !f.isExtraSeatFlight);
        }
        return [pureSortedList, impureSortedList];
      } else {
        if (
          displayOnwardFlights[displayOnwardFlights.length - 1] &&
          displayOnwardFlights[displayOnwardFlights.length - 1].originalIndex === 'viewAllFlights'
        ) {
          displayOnwardFlights.pop();
        }
        let sortedList = sortFunction([...displayOnwardFlights], sortSettings);
        if (isExtraSeatApplied) {
          sortedList = [...sortedList.filter(f => !f.isExtraSeatFlight)];
        }
        return sortedList;
      }
    } catch (err) {
      console.log('err', err);
      sendDataToNewrelic('filteredAndSortedDisplayOnwardFlights', `Failed with error: ${err}`);
      return displayOnwardFlights;
    }
  }
);

export const filteredAndSortedDisplayOnwardFlightsWithBanners = createSelector(
  [
    filteredAndSortedDisplayOnwardFlights,
    isPureImpureListingRequiredSelector,
    isOnwardFilterAppliedSelector,
    displayAlternateTimeFlightsSelector,
    displayAlternateLocationFlightsSelector,
    isDWebSelector,
    flavourSelector,
    onwardFlightsCurrentLengthSelector,
    receivedAllChunksSelector,
    internationalRoundTripSelector,
    numberOfNonStopFlightsSelector,
    smartEngageBannerSelector,
    userSelector,
    journeyTypeSelector,
    selectedFareIdentifierSelector,
    flightSearchDataSelector,
    topBannerDetailsSelector,
    getSRPAdTechBannerDetailsSelector,
    searchContextDetailsSelector,
    domesticRoundTripSelector,
    isNearbyFilterBannerVisibleSelector,
    nearbyFilterDetailsSelector,
    bannerDetailsV2Selector,
    sponsoredFlightsSelector
  ],
  (
    displayOnwardFlights,
    isPureImpureListingRequired,
    isFilterApplied,
    displayAlternateTimeFlights,
    displayAlternateLocationFlights,
    isDWeb,
    flavour,
    onwardFlightsLength,
    receivedAllChunks,
    internationalRoundTrip,
    numberOfNonStopFlights,
    smartEngageBanners,
    { isLoggedIn },
    journeyType,
    selectedFareIdentifier,
    flightSearchData,
    topBannerDetails,
    srpAdTechBannerDetails,
    searchContext,
    isDomesticRoundTrip,
    nearbyFilterVisible,
    nearbyFilterDetails,
    bannerDetails,
    sponsoredFlights
  ) => {
    try {
      const onwardFlightsWithBanner = [...displayOnwardFlights];
      if (isPureImpureListingRequired) {
        const [pureSortedList, impureSortedList] = [
          [...onwardFlightsWithBanner[0]],
          [...onwardFlightsWithBanner[1]]
        ];
        const toalSortedList = pureSortedList.length + impureSortedList.length;

        // smartengage banners
        if (smartEngageBanners?.dataList?.length && receivedAllChunks && toalSortedList > 0) {
          let index = 0;
          const BannerPositions = isDWeb ? DWEB_BANNER_POSITIONS : BANNER_POSITIONS;
          smartEngageBanners.dataList.map(smartEngageBanner => {
            let posIndex = BannerPositions[index];
            if (smartEngageBanner.data.length && toalSortedList >= posIndex) {
              if (pureSortedList.length >= posIndex || impureSortedList.length <= 0) {
                pureSortedList.splice(posIndex, 0, smartEngageBanner);
              } else {
                const pos = posIndex - pureSortedList.length;
                impureSortedList.splice(pos, 0, smartEngageBanner);
              }
              index++;
            }
          });
        }

        //should above the adtech banner
        if (nearbyFilterVisible && !isDomesticRoundTrip && toalSortedList > 0) {
          const { ibp: bannerPosition } = nearbyFilterDetails || {};
          if (bannerPosition >= 0) {
            if (pureSortedList.length >= bannerPosition || impureSortedList.length <= 0) {
              pureSortedList.splice(bannerPosition, 0, {
                originalIndex: 'nearybyFilterInlineBanner'
              });
            } else {
              const pos = bannerPosition - pureSortedList.length;
              impureSortedList.splice(pos, 0, {
                originalIndex: 'nearybyFilterInlineBanner'
              });
            }
          }
        }

        if (
          !isDomesticRoundTrip &&
          toalSortedList > 0 &&
          srpAdTechBannerDetails?.cids?.length &&
          srpAdTechBannerDetails?.indexToShowSRPAdBanner?.length
        ) {
          const pureParams = {
            list: pureSortedList
          };
          const impureParams = {
            list: impureSortedList
          };
          srpAdTechBannerDetails.cids.forEach((cids, index) => {
            const positionArr = srpAdTechBannerDetails.indexToShowSRPAdBanner;
            const dataToInsert = {
              originalIndex: 'adTechBanner',
              cids,
              searchContext
            };
            if (positionArr.hasOwnProperty(index)) {
              const insertAtPosition = positionArr[index];
              if (pureSortedList.length >= insertAtPosition || impureSortedList.length <= 0) {
                pureParams['dataToInsert'] = dataToInsert;
                pureParams['insertAtPosition'] = insertAtPosition;
                handleListWithSpillOverBanner(pureParams);
              } else {
                impureParams['dataToInsert'] = dataToInsert;
                impureParams['insertAtPosition'] = insertAtPosition - pureSortedList.length;
                handleListWithSpillOverBanner(impureParams);
              }
            }
          });
        }

        if (
          !isDomesticRoundTrip &&
          toalSortedList > 0 &&
          Object.keys(sponsoredFlights).length > 0
        ) {
          const onwardSponsoredFlightsData = sponsoredFlights[0];
          const { sponsoredTag, cardList } = onwardSponsoredFlightsData;
          const requriedCardList = cardList.reduce((acc, curr) => {
            const { rKeyList } = curr;
            const cardDetails = rKeyList.find(rkData => {
              const { rKey } = rkData;
              const index = pureSortedList.findIndex(flight => flight.fareKey === rKey);
              return index !== -1;
            });
            if (cardDetails) {
              acc[`${cardDetails.rKey}_${curr.index}`] = {
                index: curr.index,
                card: { ...cardDetails }
              };
            }
            return acc;
          }, {});

          if (Object.keys(requriedCardList).length) {
            Object.keys(requriedCardList).forEach(key => {
              const { index, card } = requriedCardList[key];
              const originalCard = pureSortedList.find(flight => flight.fareKey === card.rKey);
              pureSortedList.splice(index, 0, {
                ...originalCard,
                sponsoredData: { sponsoredTag, ...card },
                originalIndex: 'sponsoredFlight',
                originalSponsoredIndex:
                  originalCard.originalSponsoredIndex || originalCard.originalIndex
              });
            });
          }
        }

        return [pureSortedList, impureSortedList];
      }

      if (!isFilterApplied) {
        const alternateFlightsObject = {
          originalIndex: 'alternateFlights',
          hasAlternateTime: false,
          hasAlternateLocation: false,
          flights: []
        };
        if (displayAlternateTimeFlights.length > 0) {
          alternateFlightsObject.hasAlternateTime = true;
          alternateFlightsObject.flights.push(...displayAlternateTimeFlights);
        }
        if (displayAlternateLocationFlights.length > 0) {
          alternateFlightsObject.hasAlternateLocation = true;
          alternateFlightsObject.flights.push(...displayAlternateLocationFlights);
        }
        if (
          alternateFlightsObject.hasAlternateLocation ||
          alternateFlightsObject.hasAlternateTime
        ) {
          const altPos = isDWeb ? 1 : 3;
          onwardFlightsWithBanner.splice(altPos, 0, alternateFlightsObject);
        }
      }
      if (flavour === 'PWA') {
        if (isFilterApplied && onwardFlightsLength > 0 && receivedAllChunks) {
          if (internationalRoundTrip) {
            if (onwardFlightsWithBanner.length <= 2) {
              onwardFlightsWithBanner.push({ originalIndex: 'viewAllFlights' });
            }
          } else {
            if (onwardFlightsWithBanner.length <= 5) {
              onwardFlightsWithBanner.push({ originalIndex: 'viewAllFlights' });
            }
          }
        }

        if (
          onwardFlightsWithBanner &&
          onwardFlightsWithBanner.length > 5 &&
          numberOfNonStopFlights !== 0
        ) {
          let nonStopBanner = 0;

          let count = 0;
          for (let i = 0; i < onwardFlightsWithBanner.length; i++) {
            if (
              onwardFlightsWithBanner[i].flightArr &&
              onwardFlightsWithBanner[i].flightArr.length !== 1
            ) {
              count += 1;
            }
            if (count == 2) {
              nonStopBanner = i;
              break;
            }
          }

          if (nonStopBanner !== 0) {
            onwardFlightsWithBanner.splice(nonStopBanner + 1, 0, {
              data: [],
              originalIndex: 'nonstopBanner'
            });
          } else {
            onwardFlightsWithBanner.splice(3, 0, {
              data: [],
              originalIndex: 'nonstopBanner'
            });
          }
        }
      }

      // smartengage banners
      if (
        smartEngageBanners &&
        smartEngageBanners.dataList.length &&
        receivedAllChunks &&
        onwardFlightsWithBanner.length
      ) {
        let index = 0;
        const BannerPositions = isDWeb ? DWEB_BANNER_POSITIONS : BANNER_POSITIONS;
        smartEngageBanners.dataList.map(smartEngageBanner => {
          let posIndex = BannerPositions[index];
          if (smartEngageBanner.data.length && onwardFlightsWithBanner.length >= posIndex) {
            onwardFlightsWithBanner.splice(posIndex, 0, smartEngageBanner);
            index++;
          }
        });
      }

      if (flavour === 'PWA' && onwardFlightsWithBanner.length > 1) {
        if (!isLoggedIn) {
          onwardFlightsWithBanner.splice(1, 0, {
            data: [],
            originalIndex: 'signupBanner'
          });
        }
        const { flightClass } = flightSearchData;
        if (
          journeyType !== 'rt' &&
          !selectedFareIdentifier &&
          flightClass.name === 'Economy' &&
          flightClass.value === 'E'
        ) {
          onwardFlightsWithBanner.splice(0, 0, { originalIndex: 'fareTrends' });
        }
        if (topBannerDetails) {
          if (onwardFlightsWithBanner[0].originalIndex === 'fareTrends') {
            onwardFlightsWithBanner.splice(1, 0, { originalIndex: 'topBanner' });
          } else {
            onwardFlightsWithBanner.splice(0, 0, { originalIndex: 'topBanner' });
          }
        }
      }

      //should above the adtech banner
      if (nearbyFilterVisible && !isDomesticRoundTrip && onwardFlightsWithBanner.length) {
        const { ibp } = nearbyFilterDetails || {};
        if (ibp) {
          onwardFlightsWithBanner.splice(ibp, 0, {
            originalIndex: 'nearybyFilterInlineBanner'
          });
        }
      }

      if (bannerDetails && onwardFlightsWithBanner.length) {
        const inlineBanners = bannerDetails.filter(banner => banner.type === 'INLINE');
        if (inlineBanners.length) {
          inlineBanners.forEach(({ position }) => {
            onwardFlightsWithBanner.splice(position, 0, {
              originalIndex: 'inlineBanner'
            });
          });
        }
      }

      if (sponsoredFlights && Object.keys(sponsoredFlights).length !== 0) {
        const onwardFlightsData = sponsoredFlights[0];
        const { sponsoredTag, cardList } = onwardFlightsData;
        const requriedCardList = cardList.reduce((acc, curr) => {
          const { rKeyList } = curr;
          const cardDetails = rKeyList.find(rkData => {
            const { rKey } = rkData;
            const index = onwardFlightsWithBanner.findIndex(flight => flight.fareKey === rKey);
            return index !== -1;
          });
          if (cardDetails) {
            acc[`${cardDetails.rKey}_${curr.index}`] = {
              index: curr.index,
              card: { ...cardDetails }
            };
          }
          return acc;
        }, {});

        if (Object.keys(requriedCardList).length) {
          Object.keys(requriedCardList).forEach(key => {
            const { index, card } = requriedCardList[key];
            const originalCard = onwardFlightsWithBanner.find(
              flight => flight.fareKey === card.rKey
            );
            onwardFlightsWithBanner.splice(index, 0, {
              ...originalCard,
              sponsoredData: { sponsoredTag, ...card },
              originalIndex: 'sponsoredFlight',
              originalSponsoredIndex:
                originalCard.originalSponsoredIndex || originalCard.originalIndex
            });
          });
        }
      }

      if (
        ((isDWeb && !isDomesticRoundTrip) || flavour === 'PWA') &&
        onwardFlightsWithBanner.length &&
        srpAdTechBannerDetails?.cids?.length &&
        srpAdTechBannerDetails?.indexToShowSRPAdBanner?.length
      ) {
        const params = {
          list: onwardFlightsWithBanner
        };
        srpAdTechBannerDetails.cids.forEach((cids, index) => {
          params['dataToInsert'] = {
            originalIndex: 'adTechBanner',
            cids,
            searchContext
          };
          const positionArr = srpAdTechBannerDetails.indexToShowSRPAdBanner;
          if (positionArr.hasOwnProperty(index)) {
            params['insertAtPosition'] = positionArr[index];
            handleListWithSpillOverBanner(params);
          }
        });
      }
      return onwardFlightsWithBanner;
    } catch (err) {
      console.log('err', err);
      sendDataToNewrelic('filteredAndSortedDisplayOnwardFlights', `Failed with error: ${err}`);
      return displayOnwardFlights;
    }
  }
);

export const pureOnwardFlightsSelector = createSelector(
  [filteredAndSortedDisplayOnwardFlightsWithBanners, isPureImpureListingRequiredSelector],
  (displayOnwardFlights, isPureImpureListingRequired) =>
    isPureImpureListingRequired ? displayOnwardFlights[0] : []
);

export const impureOnwardFlightsSelector = createSelector(
  [filteredAndSortedDisplayOnwardFlightsWithBanners, isPureImpureListingRequiredSelector],
  (displayOnwardFlights, isPureImpureListingRequired) =>
    isPureImpureListingRequired ? displayOnwardFlights[1] : []
);

export const extraSeatFlightsSelector = createSelector(
  [displayOnwardFlightsSelector],
  onwardList => {
    return onwardList.filter(f => f.isExtraSeatFlight === true);
  }
);

export const filteredDisplayReturnFlights = createSelector(
  [
    displayReturnFlightsSelector,
    returnFilterSelector,
    isPureImpureListingRequiredSelector,
    carriersFilterSelector
  ],
  (displayReturnFlights, filterSettings, isPureImpureListingRequired, carrierFilter) => {
    if (isPureImpureListingRequired) {
      return filterPureImpureFlights(displayReturnFlights, filterSettings, carrierFilter);
    }
    return filterFunction(displayReturnFlights, filterSettings);
  }
);

export const filteredDisplayReturnFlightsLength = createSelector(
  [filteredDisplayReturnFlights, isPureImpureListingRequiredSelector],
  (displayReturnFlights, isPureImpureListingRequired) => {
    if (isPureImpureListingRequired) {
      const [pureFlights = [], impureFlights = []] = displayReturnFlights || [];
      return pureFlights.length + impureFlights.length;
    }
    if (
      displayReturnFlights[displayReturnFlights.length - 1] &&
      displayReturnFlights[displayReturnFlights.length - 1].originalIndex === 'viewAllFlights'
    ) {
      displayReturnFlights.pop();
    }
    return (displayReturnFlights || []).length;
  }
);

export const filteredAndSortedDisplayReturnFlights = createSelector(
  [filteredDisplayReturnFlights, returnSortSelector, isPureImpureListingRequiredSelector],
  (displayReturnFlights, returnSortSettings, isPureImpureListingRequired) => {
    try {
      if (isPureImpureListingRequired) {
        const [pureFlights, impureFlights] = displayReturnFlights;
        const pureSortedList = sortFunction([...pureFlights], returnSortSettings);
        const impureSortedList = sortFunction([...impureFlights], returnSortSettings);
        return [pureSortedList, impureSortedList];
      } else {
        if (
          displayReturnFlights[displayReturnFlights.length - 1] &&
          displayReturnFlights[displayReturnFlights.length - 1].originalIndex === 'viewAllFlights'
        ) {
          displayReturnFlights.pop();
        }
        let sortedList = sortFunction([...displayReturnFlights], returnSortSettings);
        return sortedList;
      }
    } catch (err) {
      console.log('err', err);
      sendDataToNewrelic('filteredAndSortedDisplayReturnFlights', `Failed with error: ${err}`);
      return displayReturnFlights;
    }
  }
);

export const filteredAndSortedDisplayReturnFlightsWithBanners = createSelector(
  [
    filteredAndSortedDisplayReturnFlights,
    isPureImpureListingRequiredSelector,
    flavourSelector,
    isReturnFilterAppliedSelector,
    returnFlightsCurrentLengthSelector,
    receivedAllChunksSelector,
    internationalRoundTripSelector,
    numberOfNonStopReturnFlightsSelector,
    userSelector,
    getSRPAdTechBannerDetailsSelector,
    searchContextDetailsSelector
  ],
  (
    displayReturnFlights,
    isPureImpureListingRequired,
    flavour,
    isFilterApplied,
    returnFlightsLength,
    receivedAllChunks,
    internationalRoundTrip,
    numberOfNonStopReturnFlights,
    { isLoggedIn },
    srpAdTechBannerDetails,
    searchContext
  ) => {
    try {
      const returnFlightsWithBanner = [...displayReturnFlights];
      if (isPureImpureListingRequired) {
        return returnFlightsWithBanner;
      } else {
        if (flavour === 'PWA' && returnFlightsWithBanner) {
          if (isFilterApplied && returnFlightsLength > 0 && receivedAllChunks) {
            if (!internationalRoundTrip && returnFlightsWithBanner.length <= 5) {
              returnFlightsWithBanner.push({ originalIndex: 'viewAllFlights' });
            }
          }
          if (returnFlightsWithBanner.length > 5 && numberOfNonStopReturnFlights !== 0) {
            let nonStopBanner = 0;
            let count = 0;
            for (let i = 0; i < returnFlightsWithBanner.length; i++) {
              if (returnFlightsWithBanner[i].flightArr.length !== 1) {
                count += 1;
              }
              if (count == 2) {
                nonStopBanner = i;
                break;
              }
            }

            if (nonStopBanner !== 0) {
              returnFlightsWithBanner.splice(nonStopBanner + 1, 0, {
                data: [],
                originalIndex: 'nonstopBanner'
              });
            } else {
              returnFlightsWithBanner.splice(3, 0, {
                data: [],
                originalIndex: 'nonstopBanner'
              });
            }
          }
          if (returnFlightsWithBanner.length > 1 && !isLoggedIn) {
            returnFlightsWithBanner.splice(1, 0, {
              data: [],
              originalIndex: 'signupBanner'
            });
          }
          if (
            returnFlightsWithBanner.length &&
            srpAdTechBannerDetails?.cids?.length &&
            srpAdTechBannerDetails?.indexToShowSRPAdBanner?.length
          ) {
            const params = {
              list: returnFlightsWithBanner
            };
            srpAdTechBannerDetails.cids.forEach((cids, index) => {
              params['dataToInsert'] = {
                originalIndex: 'adTechBanner',
                cids,
                searchContext
              };
              const positionArr = srpAdTechBannerDetails.indexToShowSRPAdBanner;
              if (positionArr.hasOwnProperty(index)) {
                params['insertAtPosition'] = positionArr[index];
                handleListWithSpillOverBanner(params);
              }
            });
          }
        }
        return returnFlightsWithBanner;
      }
    } catch (err) {
      console.log('err', err);
      sendDataToNewrelic('filteredAndSortedDisplayReturnFlights', `Failed with error: ${err}`);
      return displayReturnFlights;
    }
  }
);

export const pureReturnFlightsSelector = createSelector(
  [filteredAndSortedDisplayReturnFlightsWithBanners, isPureImpureListingRequiredSelector],
  (displayReturnFlights, isPureImpureListingRequired) =>
    isPureImpureListingRequired ? displayReturnFlights[0] : []
);

export const impureReturnFlightsSelector = createSelector(
  [filteredAndSortedDisplayReturnFlightsWithBanners, isPureImpureListingRequiredSelector],
  (displayReturnFlights, isPureImpureListingRequired) =>
    isPureImpureListingRequired ? displayReturnFlights[1] : []
);

export const selectedFlightSelector = createSelector(srpStateSelector, srp => srp.selectedFlight);

export const filteredAndCheapestSortedOnwardFlightsSelector = createSelector(
  [filteredDisplayOnwardFlights, isPureImpureListingRequiredSelector],
  (displayOnwardFlights, isPureImpureListingRequired) => {
    if (isPureImpureListingRequired) {
      const [pureFlights, impureFlights] = displayOnwardFlights;
      return sortFunction([...pureFlights, ...impureFlights], {
        currentSort: 'cheapest',
        isAscending: true,
        previousSortType: ''
      });
    }
    return sortFunction([...displayOnwardFlights], {
      currentSort: 'cheapest',
      isAscending: true,
      previousSortType: ''
    });
  }
);

export const filteredAndDurationSortedOnwardFlightsSelector = createSelector(
  [filteredDisplayOnwardFlights, isPureImpureListingRequiredSelector],
  (displayOnwardFlights, isPureImpureListingRequired) => {
    if (isPureImpureListingRequired) {
      const [pureFlights, impureFlights] = displayOnwardFlights;
      return sortFunction([...pureFlights, ...impureFlights], {
        currentSort: 'shortest',
        isAscending: true,
        previousSortType: ''
      });
    }
    return sortFunction([...displayOnwardFlights], {
      currentSort: 'shortest',
      isAscending: true,
      previousSortType: ''
    });
  }
);

export const filteredAndCheapestSortedReturnFlightsSelector = createSelector(
  [filteredDisplayReturnFlights, isPureImpureListingRequiredSelector],
  (displayReturnFlights, isPureImpureListingRequired) => {
    if (isPureImpureListingRequired) {
      const [pureFlights, impureFlights] = displayReturnFlights;
      return sortFunction([...pureFlights, ...impureFlights], {
        currentSort: 'cheapest',
        isAscending: true,
        previousSortType: ''
      });
    }
    return sortFunction([...displayReturnFlights], {
      currentSort: 'cheapest',
      isAscending: true,
      previousSortType: ''
    });
  }
);

export const domesticRoundTripDiscountMapSelector = createSelector(
  srpStateSelector,
  srp => srp.domesticRoundTripDiscountMap
);

export const domesticRoundTripDiscountSelector = createSelector(
  srpStateSelector,
  srp => srp.domesticRoundTripDiscounts
);

export const domesticRoundTripComboSelector = createSelector(
  srpStateSelector,
  srp => srp.domesticRoundTripCombo
);

export const onwardDomesticRoundTripFlightIndexSelector = createSelector(
  domesticRoundTripComboSelector,
  domesticRoundTripCombo => domesticRoundTripCombo.onwardIndex
);

export const returnDomesticRoundTripFlightIndexSelector = createSelector(
  domesticRoundTripComboSelector,
  domesticRoundTripCombo => domesticRoundTripCombo.returnIndex
);

export const onwardDomesticRoundTripSelectedFlightSelector = createSelector(
  [displayOnwardFlightsSelector, onwardDomesticRoundTripFlightIndexSelector],
  (displayOnwardFlights, onwardDomesticRoundTripFlightIndex) =>
    displayOnwardFlights[onwardDomesticRoundTripFlightIndex]
);

export const returnDomesticRoundTripSelectedFlightSelector = createSelector(
  [displayReturnFlightsSelector, returnDomesticRoundTripFlightIndexSelector],
  (displayReturnFlights, returnDomesticRoundTripFlightIndex) =>
    displayReturnFlights[returnDomesticRoundTripFlightIndex]
);

export const discountForSelectedOnwardFlightDomesticRoundTripSelector = createSelector(
  [domesticRoundTripDiscountSelector, onwardDomesticRoundTripFlightIndexSelector],
  (domesticRoundTripDiscount, onwardIndex) => {
    return domesticRoundTripDiscount.onwardDiscounts[onwardIndex]
      ? domesticRoundTripDiscount.onwardDiscounts[onwardIndex]
      : null;
  }
);

export const fareTrendsSelector = createSelector(srpStateSelector, srp => srp.fareTrendsData);

export const modifySearchWidgetSelector = createSelector(
  srpStateSelector,
  srp => srp.modifySearchWidget
);

export const headerDefaultDataFromQuerySelector = createSelector(queryParamSelector, queryParam => {
  return getFromUrlObject(queryParam);
});

export const headerSourceSelector = createSelector(
  [headerDefaultDataFromQuerySelector, airportsSelector],
  (headerDefaultDataFromQuery, airports) => {
    if (!headerDefaultDataFromQuery) {
      return null;
    }
    if (airports.length > 0) {
      const obj = getTempSrcDestObj(airports, headerDefaultDataFromQuery.source.iata);
      return obj ? obj : headerDefaultDataFromQuery.source;
    }
    return headerDefaultDataFromQuery.source;
  }
);

export const headerDestinationSelector = createSelector(
  [headerDefaultDataFromQuerySelector, airportsSelector],
  (headerDefaultDataFromQuery, airports) => {
    if (!headerDefaultDataFromQuery) {
      return null;
    }
    if (airports.length > 0) {
      const obj = getTempSrcDestObj(airports, headerDefaultDataFromQuery.destination.iata);
      return obj ? obj : headerDefaultDataFromQuery.destination;
    }
    return headerDefaultDataFromQuery.destination;
  }
);

export const headerDataSelector = createSelector(
  [headerDefaultDataFromQuerySelector, headerSourceSelector, headerDestinationSelector],
  (headerDefaultDataFromQuery, headerSource, headerDestination) => {
    if (!headerSource || !headerDestination) {
      return null;
    }
    return {
      ...headerDefaultDataFromQuery,
      departure: getDateObject(headerDefaultDataFromQuery.departure),
      arrival: getDateObject(headerDefaultDataFromQuery.arrival),
      source: headerSource,
      destination: headerDestination
    };
  }
);

export const bookingReviewDataSelector = createSelector(
  [
    onwardFlightsSelector,
    returnFlightsSelector,
    alternateTimeFlightsSelector,
    alternateLocationFlightsSelector,
    selectedFlightSelector,
    sourceSelector,
    destinationSelector,
    departureSelector,
    arrivalSelector,
    passengerSelector,
    domesticRoundTripComboSelector,
    domesticRoundTripSelector,
    travelTypeSelector,
    airportsSelector,
    carriersSelector,
    datesSelector,
    aircraftsSelector,
    discountsSelector,
    ibpSelector,
    terminalInfoSelector,
    promotionTextSelector,
    multiIndexSelector,
    refundabilitySelector,
    brandIdsSelector,
    brandNamesSelector,
    discountFaresSelector,
    discountNamesSelector,
    discountMessagesSelector,
    paValSelector,
    messageValuesSelector,
    staticInformationSelector,
    queryParamSelector,
    isRoundTripSelector,
    tokenSelector,
    directlyLandedSelector
  ],
  (
    onwardFlights,
    returnFlights,
    alternateTimeFlights,
    alternateLocationFlights,
    selectedFlight,
    source,
    destination,
    departure,
    arrival,
    passengers,
    domesticRoundTripCombo,
    domesticRoundTrip,
    travelType,
    airports,
    carriers,
    dates,
    aircrafts,
    discounts,
    ibp,
    terminalInfo,
    promotionText,
    multiIndex,
    refundability,
    brandIds,
    brandNames,
    discountFares,
    discountNames,
    discountMessages,
    paVal,
    messageValues,
    staticInformation,
    queryParam,
    isRoundTrip,
    token,
    directlyLanded
  ) => {
    try {
      let o = [];
      let r = [];
      if (selectedFlight.alternateTime) {
        o = alternateTimeFlights[selectedFlight.originalIndex];
      } else if (selectedFlight.alternateLocation) {
        o = alternateLocationFlights[selectedFlight.originalIndex];
      } else if (domesticRoundTrip) {
        o = onwardFlights[domesticRoundTripCombo.onwardIndex];
        r = returnFlights[domesticRoundTripCombo.returnIndex];
        let onwardArr = onwardFlights[domesticRoundTripCombo.onwardIndex];
        if (onwardArr) {
          o = [{ ...onwardArr[0], i: domesticRoundTripCombo.onwardIndex }];
        }
        let returnArr = returnFlights[domesticRoundTripCombo.returnIndex];
        if (returnArr) {
          r = [{ ...returnArr[0], i: domesticRoundTripCombo.returnIndex }];
        }
      } else {
        o = onwardFlights[selectedFlight.originalSponsoredIndex || selectedFlight.originalIndex];
      }
      const bookData = {
        o: o && o[0] ? (o.length > 1 ? o : [o[0]]) : [],
        r: r && r[0] ? [r[0]] : [],
        a: airports,
        c: carriers,
        d: dates,
        g: discounts,
        i: ibp,
        t: terminalInfo,
        e: aircrafts,
        px: promotionText,
        m: multiIndex,
        w: refundability,
        mc: '',
        ...(selectedFlight.bundleIndex !== null && typeof selectedFlight.bundleIndex !== 'undefined'
          ? { onward_bundle_id: selectedFlight.bundleIndex }
          : null),
        ...(brandIds.length > 0 ? { bi: brandIds } : null),
        ...(brandNames.length > 0 ? { bn: brandNames } : null),
        ...(discountFares.length > 0 ? { df: discountFares } : null),
        ...(discountNames.length > 0 ? { dn: discountNames } : null),
        ...(discountMessages.length > 0 ? { dm: discountMessages } : null),
        ...(paVal.length > 0 ? { pa: paVal } : null),
        // ...(messageValues.length > 0 ? {mv: messageValues} : null),
        ...(isObjectEmpty(staticInformation) ? null : { si: staticInformation }),
        ...(staticInformation?.ri?.rid ? { rid: staticInformation.ri.rid } : null),
        ...(directlyLanded?.searchString?.includes('sf=true') ||
        directlyLanded?.searchString?.includes('student=true')
          ? { sf: true }
          : null)
      };
      return {
        bookData,
        queryParam: 'air-' + queryParam,
        isRoundTrip,
        travelType,
        source,
        destination,
        departure,
        arrival,
        passengers,
        token
      };
    } catch (err) {
      sendDataToNewrelic('BookingReviewDataSelector', `Failed with error: ${err}`);
    }
  }
);

export const commonFareRulesRequestSelector = createSelector(
  [
    bookingReviewDataSelector,
    onwardFlightsSelector,
    returnFlightsSelector,
    alternateTimeFlightsSelector,
    alternateLocationFlightsSelector,
    selectedFlightSelector,
    flavourSelector
  ],
  (
    bookingData,
    onwardFlights,
    returnFlights,
    alternateTimeFlights,
    alternateLocationFlights,
    selectedFlight,
    flavour
  ) => {
    const bjson = JSON.parse(JSON.stringify(bookingData.bookData));
    const rid = bjson?.rid || '';

    const onwFlt = onwardFlights?.[selectedFlight?.originalIndex]?.[0] || null;
    const onwRKey = onwFlt?.rk || null;
    const retFlt = returnFlights?.[selectedFlight?.originalIndex]?.[0] || null;
    const retRKey = retFlt?.rk || null;

    bjson.o = onwardFlights[selectedFlight.originalIndex] || [];

    if (selectedFlight.alternateTime) {
      bjson.o = alternateTimeFlights[selectedFlight.originalIndex];
    } else if (selectedFlight.alternateLocation) {
      bjson.o = alternateLocationFlights[selectedFlight.originalIndex];
    }

    bjson.r = returnFlights[selectedFlight.originalIndex] || [];

    const queryParam = bookingData.queryParam.split('-');
    queryParam.pop();

    const param = {
      bjson: JSON.parse(JSON.stringify(bjson)),
      queryParamflavour: flavour,
      qry_data: '#' + queryParam.join('-')
    };

    return {
      rid,
      onwRKey,
      retRKey
    };

    return param;
  }
);

export const extraSeatInfoSelector = createSelector(
  srpStateSelector,
  srp => srp.extraSeatInfo?.es || null
);

export const priceLockInfoSelector = createSelector(
  srpStateSelector,
  srp => srp.priceLockInfo || null
);

export const priceLockOverlayDetailsSelector = createSelector(
  srpStateSelector,
  srp => srp.priceLockOverlayDetails || null
);

export const impureTitleTextSelector = createSelector(
  [staticInformationSelector],
  staticInformation => staticInformation?.oft || ''
);

export const noPureButImpureFlightsFoundTextSelector = createSelector(
  [staticInformationSelector, flightSearchDataSelector],
  (staticInformation, flightSearchData) => {
    const { source, destination } = flightSearchData;
    let replaceWith = '';
    const text = staticInformation?.nfat || '';
    if ((source?.n || source?.iata) && (destination?.n || destination?.iata)) {
      replaceWith = `${source.n || source.iata} - ${destination.n || destination.iata}`;
    }
    return text.replace(REPLACER, replaceWith);
  }
);

export const noPureImpureFlightsFoundTextSelector = createSelector(
  [staticInformationSelector, flightSearchDataSelector],
  (staticInformation, flightSearchData) => {
    const { source, destination } = flightSearchData;
    let replaceWith = '';
    const text = staticInformation?.nfdt || '';
    if ((source?.n || source?.iata) && (destination?.n || destination?.iata)) {
      replaceWith = `${source.n || source.iata} - ${destination.n || destination.iata}`;
    }
    return text.replace(REPLACER, replaceWith);
  }
);

export const priceLockCountSelector = createSelector(
  srpStateSelector,
  srp => srp.priceLockCount || 0
);

export const isNearbyFilterAppliedSelector = createSelector(
  filterSelector,
  filter => filter?.nearbyFilter?.values?.length > 0
);

// city country
export const cityCountryDestSelector = createSelector(srpStateSelector, srp => srp.cityCountryDest);

export const filterCityCountrySelector = createSelector(
  srpStateSelector,
  srp => srp.filterCityCountrySettings
);

export const onwardMaxPriceForCityCountrySelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest?.onwardMaxPrice
);

export const onwardMinPriceForCityCountrySelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest?.onwardMinPrice
);

export const onwardDurationForCityCountrySelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest?.onwardDuration
);

export const famousForCityCountrySelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest?.famousFor
);

export const showCountriesCityCountrySelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest?.showCountries
);

export const defaultCityCountryDataSelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest.defaultCityCountryData
);

export const onwardDestinationsFilterSelector = createSelector(
  [filterCityCountrySelector],
  filter => ({
    showCountries: filter.showCountries,
    onwardStops: filter.onwardStops,
    price: filter.price,
    duration: filter.duration,
    famousFor: filter.famousFor
  })
);

export const isOnwardDestinationsFilterAppliedSelector = createSelector(
  onwardDestinationsFilterSelector,
  filter => {
    const filterKeys = Object.keys(filter);
    for (let i = 0; i < filterKeys.length; i++) {
      const filterSetting = filter[filterKeys[i]];
      if (filterSetting.values.length > 0) {
        return true;
      }
    }
    return false;
  }
);

export const displayDestinationsSelector = createSelector(
  [cityCountryDestSelector],
  cityCountryDest => {
    return cityCountryDest.displayDestinations;
  }
);

export const filteredDisplayOnwardDestinations = createSelector(
  [displayDestinationsSelector, filterCityCountrySelector],
  (destinations, filterSettings) => {
    return filterFunction(destinations, filterSettings);
  }
);

export const sortDestinationSelector = createSelector(
  srpStateSelector,
  srp => srp.sortDestinationSettings
);

export const filteredAndSortedDisplayOnwardDestinations = createSelector(
  [filteredDisplayOnwardDestinations, sortDestinationSelector],
  (filteredDisplayOnwardDestinations, sortSettings) => {
    try {
      if (
        filteredDisplayOnwardDestinations &&
        filteredDisplayOnwardDestinations[filteredDisplayOnwardDestinations.length - 1] &&
        filteredDisplayOnwardDestinations[filteredDisplayOnwardDestinations.length - 1]
          .originalIndex === 'viewAllFlights'
      ) {
        filteredDisplayOnwardDestinations.pop();
      }
      let sortedList = sortFunction(filteredDisplayOnwardDestinations, sortSettings, true);
      let updateSortedList = [];
      let listOfCities = [];
      if (sortedList) {
        for (let i = 0; i < sortedList.length; i++) {
          if (!listOfCities.includes(sortedList[i]?.toCity)) {
            listOfCities.push(sortedList[i]?.toCity);
            updateSortedList.push({ ...sortedList[i] });
          }
        }
      }
      if (updateSortedList.length > 0) {
        sortedList = [...updateSortedList];
      }
      return sortedList;
    } catch (err) {
      console.log('err', err);
      return filteredDisplayOnwardDestinations;
    }
  }
);

export const selectedDestinationSelector = createSelector(
  srpStateSelector,
  srp => srp.selectedDestination
);

export const filteredAndSortedDisplayDetailsFlights = createSelector(
  [displayOnwardFlightsSelector, sortSelector],
  (displayOnwardFlights, sortFlightsSettings) => {
    try {
      if (
        displayOnwardFlights &&
        displayOnwardFlights[displayOnwardFlights.length - 1] &&
        displayOnwardFlights[displayOnwardFlights.length - 1].originalIndex === 'viewAllFlights'
      ) {
        displayOnwardFlights.pop();
      }
      let sortedList = sortFunction(displayOnwardFlights, sortFlightsSettings);
      return sortedList;
    } catch (err) {
      console.log('err', err);
      return displayOnwardFlights;
    }
  }
);

export const constructThorUrlForCityCountry = createSelector(
  [
    flightSearchDataSelector,
    selectedDestinationSelector,
    defaultCityCountryDataSelector,
    flavourSelector
  ],
  (flightSearchData, selectedDestination, defaultCityCountryData, flavour) => {
    const query = getSrpQueryParamsForCityCountry(
      flightSearchData,
      selectedDestination,
      defaultCityCountryData,
      true
    );
    if (!query) {
      return null;
    }
    const { travelType } = defaultCityCountryData;

    return `${__GI_CB__}/v2/thor/rest/flight/search/${
      travelType === 'international' ? 'int' : ''
    }?application=fbs&flavour=${flavour}&mime=html&script=y&actionData=[{%22query%22:%22${query}%22}]&slotfl=y1&nearbyfl=y1`;
  }
);

export const cityCountryAirportInfoSelector = createSelector(
  cityCountryDestSelector,
  cityCountryDest => cityCountryDest?.cityCountryAirports
);

export const urlCityCountryQuerySelector = createSelector(
  [flightSearchDataSelector, selectedDestinationSelector, defaultCityCountryDataSelector],
  (flightSearchData, selectedDestination, defaultCityCountryData) => {
    return getSrpQueryParamsForCityCountry(
      flightSearchData,
      selectedDestination,
      defaultCityCountryData
    );
  }
);

export const isCityCountrySelector = createSelector(srpStateSelector, srp => {
  return srp.isCityCountry;
});

export const srpOmnitureInfoSelector = createSelector(srpStateSelector, srp => {
  return srp?.srpOmnitureInfo;
});

export const getAppliedFilterList = createSelector(srpStateSelector, srp => {
  return srp.appliedFilterList;
});

export const lastFilterAppliedSelector = createSelector(srpStateSelector, srp => {
  return srp.lastFilterApplied || '';
});

export const srpMissedOmnitureEventsSelector = createSelector(srpStateSelector, srp => {
  return srp.srpMissedOmnitureEvents;
});

export const hasMultiCheckinSelector = createSelector(srpStateSelector, srp => srp.hasMultiCheckin);

export const isPaymentPageLoadingSelector = createSelector(
  srpStateSelector,
  srp => srp.isPaymentPageLoading
);

export const isFlightSelectedSelector = createSelector(srpStateSelector, srp => {
  const { selectedFlight } = srp;
  return !isObjectEmpty(selectedFlight);
});

export const srpPopupErrorSelector = createSelector(srpStateSelector, srp => {
  return {
    srpErrorPopupText: srp.srpErrorPopupText,
    srpErrorPopupHeading: srp.srpErrorPopupHeading
  };
});

export const staticTextSelector = createSelector(srpStateSelector, srp => {
  return srp?.staticText || {};
});

export const postSearchRkeysDataSelector = createSelector(
  srpStateSelector,
  srp => srp?.postSearchData?.rKeyData || {}
);

export const isPriceLockEnabledSelector = createSelector(
  srpStateSelector,
  srp => srp?.postSearchData?.ipe || false
);

export const showPriceLockBannerSelector = createSelector(
  srpStateSelector,
  srp => srp?.postSearchData?.showPlBnr ?? true
);

export const priceLockReviewUrlSelector = createSelector(
  srpStateSelector,
  srp => srp?.postSearchData?.flp || false
);

export const flightExpertDataSelector = createSelector(
  srpStateSelector,
  srp => srp?.postSearchData?.fle || {}
);

export const priceLockReviewDetailsSelector = createSelector(srpStateSelector, srp => {
  return srp.priceLockReviewDetails;
});

export const isPageLoadingSelector = createSelector(srpStateSelector, srp => srp.isPageLoading);

export const onwardFlightFareFamilyIndexSelector = createSelector(
  srpStateSelector,
  srp => srp && srp.onwardFlightFareFamilyIndex
);

export const returnFlightFareFamilyIndexSelector = createSelector(
  srpStateSelector,
  srp => srp && srp.returnFlightFareFamilyIndex
);

export const ridSelector = createSelector(
  staticInformationSelector,
  staticInformation => staticInformation?.ri?.rid
);

export const combinedPersuasionSelector = createSelector(
  srpStateSelector,
  srp => srp.combinedPersuasion
);

export const flightExpertBannerSelector = createSelector(
  brpStateSelector,
  brp => brp?.flightsExpertBanner || {}
);

export const repriceRequestSelector = createSelector(
  [
    bookingReviewDataSelector,
    flavourSelector,
    onwardFlightFareFamilyIndexSelector, //only for dsrt
    returnFlightFareFamilyIndexSelector, //only for dsrt
    selectedFlightSelector // applicable for all except dsrt
  ],
  (
    bookingReviewData,
    flavour,
    onwardFlightFareFamilyIndex,
    returnFlightFareFamilyIndex,
    selectedFlight
  ) => {
    const { rid = '', o = [], r = [] } = bookingReviewData.bookData || {};

    const rcoms = {};
    if (o && o.length) {
      const onwFlt = o[0];
      rcoms[onwFlt.cid] = getFareKey(onwFlt, selectedFlight, onwardFlightFareFamilyIndex);
    }
    if (r && r.length) {
      const retFlt = r[0];
      rcoms[retFlt.cid] = getFareKey(retFlt, selectedFlight, returnFlightFareFamilyIndex);
    }

    if (!Object.keys(rcoms).length) {
      return null;
    }

    const urlParams = searchStringToObject(window?.location?.search);

    return {
      ...urlParams,
      v: 1.6,
      flavour,
      query_data: bookingReviewData?.queryParam?.split('-').slice(0, 9).join('-'),
      rid,
      rcoms: JSON.stringify(rcoms),
      rkeys: JSON.stringify(Object.values(rcoms))
    };
  }
);
